/**
 * Per-station FIFO pipeline for OHT ↔ stocker transfer choreography.
 *
 * Socket frames can arrive faster than wall-clock animation. This store buffers
 * transfer intents, runs one station animation at a time, and gates inventory
 * until both the OHT handoff and the internal crane/lifter motion complete.
 */

export type PipelineAction = 'PICKUP' | 'DROP';

export type PipelineEvent = {
  eventId: string;
  stationId: string;
  portId: string;
  action: PipelineAction;
  foupId: string;
  ohtId: string;
  /** Stocker transfers wait for crane/lifter; equipment only waits for OHT. */
  kind: 'stocker' | 'equipment';
  enqueuedAt: number;
  ohtPhaseComplete: boolean;
  cranePhaseComplete: boolean;
};

type StationPipeline = {
  queue: PipelineEvent[];
  active: PipelineEvent | null;
};

type Listener = () => void;

const MAX_QUEUE_PER_STATION = 48;
const MAX_COMPLETED_IDS = 512;

function isStockerStation(stationId: string, portId: string): boolean {
  if (/^(?:ST|STOCKER)[-_]/i.test(stationId)) return true;
  return /^(?:ST|STOCKER)[-_]/i.test(portId);
}

export class TransferAnimationPipelineStore {
  private readonly byStation = new Map<string, StationPipeline>();
  private readonly completedIds = new Set<string>();
  private readonly completedOrder: string[] = [];
  private listeners = new Set<Listener>();
  private sequence = 0;

  reset(): void {
    this.byStation.clear();
    this.completedIds.clear();
    this.completedOrder.length = 0;
    this.sequence = 0;
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Ingest a streamed transfer_event (deduped, FIFO per station). */
  enqueue(input: {
    eventId: string;
    stationId: string;
    portId: string;
    action: PipelineAction;
    foupId: string;
    ohtId: string;
    stockerId?: string | null;
    toolId?: string | null;
  }): boolean {
    const { eventId, stationId, portId, action, foupId, ohtId } = input;
    if (!stationId || !eventId) return false;
    if (this.completedIds.has(eventId)) return false;

    const pipe = this.pipeline(stationId);
    if (pipe.queue.some((e) => e.eventId === eventId)) return false;
    if (pipe.active?.eventId === eventId) return false;

    const kind: PipelineEvent['kind'] =
      input.stockerId || isStockerStation(stationId, portId) ? 'stocker' : 'equipment';

    if (pipe.queue.length >= MAX_QUEUE_PER_STATION) {
      pipe.queue.shift();
    }

    pipe.queue.push({
      eventId,
      stationId,
      portId,
      action,
      foupId,
      ohtId,
      kind,
      enqueuedAt: ++this.sequence,
      ohtPhaseComplete: false,
      cranePhaseComplete: kind === 'equipment',
    });
    this.notify();
    return true;
  }

  /**
   * Scheduler may arm a wall hold only for the queue head while no other event
   * is active on this station.
   */
  canArmTransfer(stationId: string, eventId: string): boolean {
    if (!stationId || !eventId) return true;
    if (this.completedIds.has(eventId)) return false;

    const pipe = this.pipeline(stationId);
    if (pipe.active && pipe.active.eventId !== eventId) return false;

    const head = pipe.queue[0];
    if (!head) return pipe.active?.eventId === eventId || !pipe.active;
    return head.eventId === eventId;
  }

  /** Called when the scheduler arms a wall hold for this transfer. */
  markArmed(
    stationId: string,
    eventId: string,
    hints?: { kind?: PipelineEvent['kind']; portId?: string }
  ): void {
    const pipe = this.pipeline(stationId);
    const idx = pipe.queue.findIndex((e) => e.eventId === eventId);
    if (idx >= 0) {
      const [event] = pipe.queue.splice(idx, 1);
      pipe.active = event;
    } else if (!pipe.active || pipe.active.eventId === eventId) {
      const kind =
        hints?.kind ??
        (isStockerStation(stationId, hints?.portId ?? stationId) ? 'stocker' : 'equipment');
      pipe.active = {
        eventId,
        stationId,
        portId: hints?.portId ?? '',
        action: 'DROP',
        foupId: '',
        ohtId: '',
        kind,
        enqueuedAt: ++this.sequence,
        ohtPhaseComplete: false,
        cranePhaseComplete: kind === 'equipment',
      };
    }
    this.notify();
  }

  markOhtPhaseComplete(stationId: string, eventId: string): void {
    const active = this.pipeline(stationId).active;
    if (!active || active.eventId !== eventId) return;
    active.ohtPhaseComplete = true;
    this.tryFinishStation(stationId);
    this.notify();
  }

  markCranePhaseComplete(stationId: string, eventId: string): void {
    const active = this.pipeline(stationId).active;
    if (!active || active.eventId !== eventId) return;
    active.cranePhaseComplete = true;
    this.tryFinishStation(stationId);
    this.notify();
  }

  isAwaitingCrane(stationId: string, eventId: string): boolean {
    const active = this.pipeline(stationId).active;
    if (!active || active.eventId !== eventId) return false;
    if (active.kind !== 'stocker') return false;
    return active.ohtPhaseComplete && !active.cranePhaseComplete;
  }

  shouldHoldInventory(stationId: string): boolean {
    const pipe = this.byStation.get(stationId);
    if (!pipe) return false;
    if (pipe.queue.length > 0) return true;
    if (!pipe.active) return false;
    return !this.isEventFullyComplete(pipe.active);
  }

  getInventoryHoldStockerIds(): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const [stationId, pipe] of this.byStation) {
      if (pipe.queue.length > 0 || (pipe.active && !this.isEventFullyComplete(pipe.active))) {
        ids.add(stationId);
      }
    }
    return ids;
  }

  getActiveEventId(stationId: string): string | null {
    return this.pipeline(stationId).active?.eventId ?? null;
  }

  /** Drop a stale or late event so it cannot block the station queue. */
  skipEvent(stationId: string, eventId: string): void {
    if (!stationId || !eventId) return;
    const pipe = this.pipeline(stationId);
    pipe.queue = pipe.queue.filter((event) => event.eventId !== eventId);
    if (pipe.active?.eventId === eventId) {
      this.rememberCompleted(eventId);
      pipe.active = null;
    } else {
      this.rememberCompleted(eventId);
    }
    this.notify();
  }

  private pipeline(stationId: string): StationPipeline {
    let pipe = this.byStation.get(stationId);
    if (!pipe) {
      pipe = { queue: [], active: null };
      this.byStation.set(stationId, pipe);
    }
    return pipe;
  }

  private isEventFullyComplete(event: PipelineEvent): boolean {
    return event.ohtPhaseComplete && event.cranePhaseComplete;
  }

  private tryFinishStation(stationId: string): void {
    const pipe = this.pipeline(stationId);
    const active = pipe.active;
    if (!active || !this.isEventFullyComplete(active)) return;

    this.rememberCompleted(active.eventId);
    pipe.active = null;
  }

  private rememberCompleted(eventId: string): void {
    if (this.completedIds.has(eventId)) return;
    this.completedIds.add(eventId);
    this.completedOrder.push(eventId);
    while (this.completedOrder.length > MAX_COMPLETED_IDS) {
      const stale = this.completedOrder.shift();
      if (stale) this.completedIds.delete(stale);
    }
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export const transferAnimationPipelineStore = new TransferAnimationPipelineStore();
